import { createSortOption } from "./utils/sorting"
import { thumbnailDecorator } from "@nosto/search-js/thumbnails"
import { priceDecorator } from "@nosto/search-js/currencies"
import { AutocompleteConfig } from "@nosto/search-js/preact/autocomplete"
import { SerpConfig } from "@nosto/search-js/preact/serp"
import { handleDecorator } from "./decorators"
import { CategoryConfig } from "@nosto/search-js/preact/category"
import { SearchQuery } from "@nosto/nosto-js/client"
import { tagging } from "./mapping/tagging"
import { pipe } from "./utils/pipe"

export const sizes = [24, 48, 72]

export const sortOptions = [
  createSortOption("featured", "Featured", { field: "_score", order: "desc" }),
  // Creation dates are all the install date. The custom field is numeric, `productId` is a keyword.
  createSortOption("newest", "New", { field: "customFields.product-id", order: "desc" }),
  createSortOption("-price", "Price descending", { field: "price", order: "desc" }),
  createSortOption("price", "Price ascending", { field: "price", order: "asc" }),
  createSortOption("stats.revenue", "Best selling", { field: "stats.buys", order: "desc" }),
]

/**
 * Enable or disable infinite scroll for search results
 */
export const infiniteScroll = false

/**
 * Enable or disable skeleton loading state for search results
 */
export const skeletonLoading = true

/**
 * Enable or disable search redirect on search form submit
 */
export const redirectOnSearch = true

export const shopifyMode = !!window.Shopify

/**
 * Default sort option for search results and categories
 */
export const defaultSort = sortOptions[0]

/**
 * Default page size for search results and categories
 */
export const defaultSize = sizes[0]

/**
 * Path to the search page
 */
export const searchPath = "/searchresults.html"

/**
 * Popular searches always shown in the autocomplete before the user starts typing
 */
export const hardcodedPopularSearches = ["Chef Knife", "Pocket Knife", "Kitchen Set", "Sharpener", "Sale"]

/**
 * CSS selectors for the Injected mode
 */
export const selectors = {
  // autocomplete dropdown container
  dropdown: ".main-header",
  // search input field
  searchInput: "#search_query",
  // search form element selector
  searchForm: "#search-form",
  // container for actual search/category results
  // results: "#serp"
}

/**
 * Base hit decorator definitions for the search results.
 *
 * Decorators are transformer functions applied in order to all results returned
 * from the search backend. You may define your own decorators using `handleDecorator`
 * as an example.
 * The type for decorated products is defined in `src/types.ts`.
 */
export const hitDecorators = [handleDecorator, priceDecorator()] as const

/**
 * Shared configuration for all page types (Autocomplete, Category, Search)
 */
export const baseConfig = {
  search: {
    hitDecorators
  },
  queryModifications: withBaseConfig
}

function withBaseConfig(query: SearchQuery) {
  return {
    ...query,
    products: {
      size: defaultSize,
      ...query.products
      // uncomment for exchange rates based multi-currency support
      //currency: tagging.variation() ?? defaultCurrency

      // uncomment for price variation based multi-currency support
      //variationId: tagging.variation() ?? defaultCurrency
    }
  } satisfies SearchQuery
}

/**
 * Number of products shown in the autocomplete dropdown
 */
export const autocompleteProductsSize = 8

/**
 * Autocomplete configuration
 */

export const autocompleteConfig = {
  ...baseConfig,
  memoryCache: true,
  historyEnabled: true,
  historySize: 10,
  search: {
    hitDecorators: [
      ...hitDecorators,
      thumbnailDecorator({ size: "8" }) // 400x400
    ]
  },
  queryModifications: pipe<SearchQuery>(withBaseConfig, withAutocompleteDefaults)
} satisfies AutocompleteConfig

function withAutocompleteDefaults(query: SearchQuery) {
  return {
    ...query,
    products: {
      ...query.products,
      size: autocompleteProductsSize
    },
    keywords: {
      fields: ["keyword", "_highlight.keyword"],
      size: 5,
      facets: ["*"]
    },
    popularSearches: {
      fields: ["query"],
      size: 5
    },
    categories: {
      fields: ["name", "url"],
      size: 5
    }
  } satisfies SearchQuery
}

/**
 * Category page configuration
 */

export const categoryConfig = {
  ...baseConfig,
  persistentSearchCache: false,
  preservePageScroll: false,
  queryModifications: pipe(withBaseConfig, withCategoryMetadata)
} satisfies CategoryConfig

function withCategoryMetadata(query: SearchQuery) {
  return {
    ...query,
    products: {
      categoryId: tagging.categoryId(),
      categoryPath: tagging.categoryPath(),
      ...query.products
    }
  } satisfies SearchQuery
}

/**
 * Search result page (serp) configuration
 */
export const serpConfig = {
  ...baseConfig,
  persistentSearchCache: false,
  preservePageScroll: false
} satisfies SerpConfig
